import * as React from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { COLOR_PRIMARY, COLOR_SUCCESS } from 'src/app/constants/color';
import { getRevenueScale, RevenueScale, toCountFormat, toJutaFormat } from './Dashboard.formatters';
import { DashboardTrendPoint } from './Dashboard.queries';

interface TrendChartProps {
  points: DashboardTrendPoint[];
}

interface TrendChartState {
  isChartVisible: boolean;
}

interface ChartDatum {
  label: string;
  orders: number;
  scaledRevenue: number;
  revenue: number;
}

const CHART_HEIGHT = 300;
const ORDERS_SERIES_NAME = 'Orders';

/**
 * The tooltip prints this instead of the series name. The series name carries the axis unit
 * ("Revenue (Rp miliar)") because the axis needs it, but the tooltip states its own unit per value,
 * so reusing the name there produced the contradiction "Revenue (Rp miliar) : Rp177.1 juta".
 */
const REVENUE_TOOLTIP_NAME = 'Revenue';

/**
 * recharts' ResponsiveContainer measures the DOM, so it must not run during the SSR build.
 * The chart is mounted only once the component is on the client.
 */
export class TrendChart extends React.Component<TrendChartProps, TrendChartState> {
  constructor(props: TrendChartProps) {
    super(props);
    this.state = { isChartVisible: false };
  }

  componentDidMount() {
    this.setState({ isChartVisible: true });
  }

  render() {
    const { points } = this.props;

    if (!this.state.isChartVisible) {
      return <div className="Dashboard__chart-placeholder" />;
    }

    if (points.length === 0) {
      return <div className="Dashboard__empty">No data in this period</div>;
    }

    const scale = this.getScale();
    const revenueSeriesName = `Revenue (Rp ${scale.unitLabel})`;
    const data: ChartDatum[] = points.map((point: DashboardTrendPoint) => ({
      label: point.label,
      orders: point.orders,
      revenue: point.revenue,
      scaledRevenue: point.revenue / scale.divisor
    }));

    return (
      <ResponsiveContainer height={CHART_HEIGHT} width="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#eef1f6" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: '#717171', fontSize: 12 }} />
          <YAxis tick={{ fill: '#717171', fontSize: 12 }} yAxisId="orders" />
          <YAxis orientation="right" tick={{ fill: '#717171', fontSize: 12 }} yAxisId="revenue" />
          <Tooltip formatter={this.formatTooltipValue} />
          <Legend />
          <Bar barSize={36} dataKey="orders" fill={COLOR_PRIMARY} name={ORDERS_SERIES_NAME} yAxisId="orders" />
          <Line
            dataKey="scaledRevenue"
            dot={{ r: 4 }}
            name={revenueSeriesName}
            stroke={COLOR_SUCCESS}
            strokeWidth={2}
            type="monotone"
            yAxisId="revenue"
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  private readonly getScale = (): RevenueScale =>
    getRevenueScale(
      this.props.points.reduce((max: number, point: DashboardTrendPoint) => Math.max(max, point.revenue), 0)
    );

  /**
   * Formats each point on its own scale, not the axis scale.
   *
   * The axis has to commit to one unit for the whole series, so a month worth Rp145 juta plotted
   * against a Rp1.7 miliar peak collapses to "Rp0.1 miliar". Reading the unscaled `revenue` off the
   * hovered datum and formatting it independently keeps every tooltip exact.
   */
  private readonly formatTooltipValue = (value: any, name: string, item: any) => {
    if (name === ORDERS_SERIES_NAME) {
      return [toCountFormat(Number(value)), name];
    }
    const rawRevenue = item && item.payload ? Number(item.payload.revenue) : Number(value) * this.getScale().divisor;
    return [toJutaFormat(rawRevenue), REVENUE_TOOLTIP_NAME];
  };
}
